import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../services/api';

const Container = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

const Title = styled.h1`
  font-size: 2.1rem;
  margin: 0 0 0.6rem;
`;

const Subtitle = styled.p`
  margin: 0 0 1.6rem;
  font-size: 1.05rem;
  color: #9ca3af;
`;

const ImageGrid = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ImageThumb = styled.img`
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #374151;
`;

const FieldList = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 0.35rem;
`;

const FieldLabel = styled.dt`
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
`;

const FieldValue = styled.dd`
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: #e5e7eb;
`;

const SectionHeading = styled.h2`
  margin: 1.25rem 0 0.5rem;
  font-size: 1.15rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #4a90e2;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #f3f4f6;
  color: #374151;
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  font-size: 1rem;
  color: #9ca3af;
`;

const Message = styled.p`
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #9ca3af;
`;

const BookingRequestDetail = () => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/bookings/${id}`);
        setBooking(res.data);
      } catch (err) {
        console.error('Failed to load booking request', err);
        const message = err?.response?.data?.message || 'Could not load this booking request.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBooking();
    }
  }, [id]);

  if (loading) {
    return (
      <Container>
        <BackLink to="/dashboard">← Back to dashboard</BackLink>
        <Title>Booking request</Title>
        <Subtitle>Loading booking details…</Subtitle>
      </Container>
    );
  }

  if (error || !booking) {
    return (
      <Container>
        <BackLink to="/dashboard">← Back to dashboard</BackLink>
        <Title>Booking request</Title>
        <Message>{error || 'Booking request not found.'}</Message>
      </Container>
    );
  }

  const created = booking.createdAt ? new Date(booking.createdAt) : null;

  return (
    <Container>
      <BackLink to="/dashboard">← Back to dashboard</BackLink>
      <Title>Booking request from {booking.name}</Title>
      <Subtitle>
        Sent {created ? created.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'recently'}
      </Subtitle>

      <Meta>
        <div>
          <StatusPill>{booking.status || 'new'}</StatusPill>
        </div>
      </Meta>

      <SectionHeading>Client info</SectionHeading>
      <FieldList>
        <FieldLabel>Client name</FieldLabel>
        <FieldValue>{booking.name}</FieldValue>

        <FieldLabel>Email</FieldLabel>
        <FieldValue>{booking.email}</FieldValue>

        <FieldLabel>Phone</FieldLabel>
        <FieldValue>{booking.phone}</FieldValue>
      </FieldList>

      <SectionHeading>Tattoo details</SectionHeading>
      <FieldList>
        <FieldLabel>Placement</FieldLabel>
        <FieldValue>{booking.placement}</FieldValue>

        <FieldLabel>Rough size</FieldLabel>
        <FieldValue>{booking.size}</FieldValue>

        <FieldLabel>Style / idea</FieldLabel>
        <FieldValue>{booking.styleIdea}</FieldValue>

        <FieldLabel>Budget</FieldLabel>
        <FieldValue>{booking.budget || 'Not specified'}</FieldValue>
      </FieldList>

      <SectionHeading>Scheduling</SectionHeading>
      <FieldList>
        <FieldLabel>Day / time preferences</FieldLabel>
        <FieldValue>{booking.availability || 'Not specified'}</FieldValue>
      </FieldList>

      <SectionHeading>Other</SectionHeading>
      <FieldList>
        <FieldLabel>Additional notes from client</FieldLabel>
        <FieldValue>{booking.extraNotes || 'None provided'}</FieldValue>

        <FieldLabel>Disclaimer confirmed</FieldLabel>
        <FieldValue>{booking.readDisclaimer ? 'Yes' : 'No'}</FieldValue>
      </FieldList>

      {Array.isArray(booking.designReferences) && booking.designReferences.length > 0 && (
        <>
          <FieldLabel>Reference photos</FieldLabel>
          <ImageGrid>
            {booking.designReferences.map((url) => (
              <ImageThumb key={url} src={url} alt="Reference" />
            ))}
          </ImageGrid>
        </>
      )}

      {Array.isArray(booking.internalNotes) && booking.internalNotes.length > 0 && (
        <>
          <FieldLabel>Internal notes</FieldLabel>
          <FieldValue as="div">
            {booking.internalNotes.map((note) => {
              const noteDate = note.createdAt ? new Date(note.createdAt) : null;
              return (
                <div key={note._id || `${note.content}-${noteDate?.toISOString?.() || ''}`} style={{ marginBottom: '0.5rem' }}>
                  {noteDate && (
                    <span style={{ color: '#9ca3af', fontSize: '0.8rem', marginRight: '0.35rem' }}>
                      {noteDate.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  )}
                  <span>{note.content}</span>
                </div>
              );
            })}
          </FieldValue>
        </>
      )}
    </Container>
  );
};

export default BookingRequestDetail;
